/**
 * Shared formatting for trace copy.
 *
 * Tour times render in the PROPERTY's timezone, not the viewer's: the messages beside them quote
 * the property's local clock ("Thursday at 12:00 PM"), so an owner watching a Nevada property from
 * New York must not read "3:00 PM" on the event row right under it. The zone tag is appended only
 * when the viewer's own clock disagrees, so same-zone owners see no extra noise.
 *
 * Entry timestamps (a message's "10:02 AM", the day dividers) stay in the viewer's local time —
 * those answer "when did this happen to me", and match the rest of the investor app.
 */

const dayOptions: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/** "PDT" / "GMT+3" for an instant in a zone — undefined timeZone means the viewer's own. */
const zoneTag = (date: Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value ?? '';

/** " PDT" when the property's clock differs from the viewer's for this instant, "" when it agrees. */
const zoneSuffix = (date: Date, timeZone?: string): string => {
  if (!timeZone) {
    return '';
  }
  const tag = zoneTag(date, timeZone);

  return tag && tag !== zoneTag(date) ? ` ${tag}` : '';
};

export const formatTraceTime = (iso: string, timeZone?: string): string =>
  new Date(iso).toLocaleTimeString('en-US', { ...timeOptions, timeZone });

/** "Sat, Aug 15, 10:00 AM PDT" — for tour times inside panel/event copy. */
export const formatTraceDayTime = (iso: string, timeZone?: string): string => {
  const date = new Date(iso);

  return `${date.toLocaleDateString('en-US', { ...dayOptions, timeZone })}, ${formatTraceTime(iso, timeZone)}${zoneSuffix(date, timeZone)}`;
};

/**
 * "Sat 10:00 AM" — compact form for chip labels, where every character fights for space, so the
 * zone tag is dropped here; the panel underneath spells the full time out.
 */
export const formatTraceChipTime = (iso: string, timeZone?: string): string =>
  `${new Date(iso).toLocaleDateString('en-US', { weekday: 'short', timeZone })} ${formatTraceTime(iso, timeZone)}`;

/** "SOME_STEP_KEY" / "some-kind" → "Some step key" — the never-crash fallback for unknown ids. */
export const humanizeTraceKey = (key: string): string => {
  const spaced = String(key).replace(/[-_]+/g, ' ').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const truncateTraceText = (text: string | undefined, maxLength: number): string => {
  if (!text) {
    return '';
  }
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
};
